import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import RotateDevice from "../../components/RotateDevice/RotateDevice";
import Lf23Loader from "../../components/Lf23Loader";
import { API_URL, authHeaders, FullscreenBtn, KpiCard, ico } from "./comuni";
import {
    AggiornaBtn, CardPercorso, RigaPercorso, TestataPercorsi, ViewToggle, useEscChiude, isAperto,
} from "./percorsiCondivisi";

/**
 * Sotto-tab "Oggi" del tab Percorsi (task 517): i giri con data di oggi in QUALUNQUE stato
 * (da fare, in lavorazione, conclusi), più quelli di domani già iniziati — il notturno parte
 * alle 22:00 della sera prima ma porta la data del giorno in cui finisce. La regola sta nel
 * backend (percorsiGiornataService.sezionePercorso), qui si mostra e basta.
 * Una sola tabella (prima erano "in lavorazione" + conclusi): l'ordine lo dà il backend,
 * prima chi è in strada, poi chi deve partire, in fondo i giri chiusi.
 *
 * Props: viewMode/onViewMode (tabella/card, stato nel contenitore), onDetail (modale dettaglio
 * del contenitore), azioniDi(p, opts) (bottoni riga con i gestori del contenitore).
 */
const SezioneOggi = ({ zona, viewMode, onViewMode, onDetail, azioniDi }) => {
    const [dati, setDati] = useState([]);
    const [stats, setStats] = useState({ totali: 0, daFare: 0, inLavorazione: 0, conclusi: 0 });
    const [loading, setLoading] = useState(false);
    const [cerca, setCerca] = useState("");
    const [fs, setFs] = useState(false);

    const fetchOggi = useCallback(async (opts = {}) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/percorsi/oggi`, {
                headers: authHeaders(),
                params: { zona, refresh: opts.refresh ? 1 : undefined },
            });
            setDati(res.data?.data ?? []);
            setStats(res.data?.stats ?? { totali: 0, daFare: 0, inLavorazione: 0, conclusi: 0 });
        } catch (err) {
            console.error("Errore nel fetch percorsi di oggi:", err);
            setDati([]);
        } finally {
            setLoading(false);
        }
    }, [zona]);

    useEffect(() => { fetchOggi(); }, [fetchOggi]);

    const chiudiFs = useCallback(() => setFs(false), []);
    useEscChiude(fs, chiudiFs);

    // Ricerca lato client: sono 15-20 righe, inutile tornare al backend a ogni tasto.
    const visibili = useMemo(() => {
        const s = cerca.trim().toLowerCase();
        if (!s) return dati;
        return dati.filter((p) =>
            (p.nome || "").toLowerCase().includes(s) ||
            (p.customId || "").toLowerCase().includes(s) ||
            (p.assegnatario?.nome || "").toLowerCase().includes(s));
    }, [dati, cerca]);

    // Report PDF solo sui giri conclusi: uno da fare / in corso non ha ancora un report.
    const azioni = (p) => azioniDi(p, { conReport: !isAperto(p.stato) });

    const titolo = (
        <>Percorsi di oggi <span className="gp-section-count">{loading && dati.length === 0 ? "…" : visibili.length}</span></>
    );
    const vuoto = dati.length === 0
        ? "Nessun percorso per oggi."
        : "Nessun percorso corrisponde alla ricerca.";

    return (
        <>
            <div className="gp-toolbar-row">
                <div className="toolbar">
                    <div className="search-box">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                        <input type="text" placeholder="Cerca percorso, customId, assegnatario..." value={cerca} onChange={(e) => setCerca(e.target.value)} />
                    </div>
                    <span className="gp-tb-break" aria-hidden="true" />
                    <div className="spacer" />
                    <AggiornaBtn onClick={() => fetchOggi({ refresh: true })} />
                </div>
            </div>

            {loading && dati.length === 0 ? (
                <div className="gp-loading">
                    <Lf23Loader testo="Caricamento percorsi di oggi..." />
                </div>
            ) : (
            <>
            <div className="stats-grid">
                <KpiCard tone="blue" label="Percorsi di oggi" valore={stats.totali}
                    titolo="Giri con data di oggi, più i notturni di domani già partiti"
                    icona={ico(<path d="M9 20l-5.5-2.5V4L9 6.5m0 13.5l6-3m-6 3V6.5m6 10.5l5.5 2.5V6L15 3.5m0 13.5V3.5" />)} />
                <KpiCard tone="slate" label="Da fare" valore={stats.daFare}
                    titolo="Pubblicati su Interacta ma non ancora iniziati dall'operatore"
                    icona={ico(<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>)} />
                <KpiCard tone="lime" label="In lavorazione" valore={stats.inLavorazione}
                    titolo="Iniziati (prima foto scattata) e non ancora chiusi"
                    icona={ico(<polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />)} />
                <KpiCard tone="green" label="Conclusi" valore={stats.conclusi}
                    titolo="Chiusi dall'operatore: completati, parziali o non eseguiti"
                    icona={ico(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>)} />
            </div>

            <ViewToggle viewMode={viewMode} onChange={onViewMode} />

            {viewMode === "table" ? (
                <div className={`table-container${fs ? " gp-fullscreen-table" : ""}`}>
                    <RotateDevice active={fs} maxWidth={1024} />
                    <div className="gp-table-topbar">
                        <div className="gp-table-title">{titolo}</div>
                        <FullscreenBtn attivo={fs} onClick={() => setFs((v) => !v)} />
                    </div>
                    <div className="table-scroll">
                        <table>
                            <TestataPercorsi />
                            <tbody>
                                {visibili.map((p) => <RigaPercorso key={p.id} p={p} onDetail={onDetail} azioni={azioni(p)} />)}
                            </tbody>
                        </table>
                        {visibili.length === 0 && <div className="gp-empty">{vuoto}</div>}
                    </div>
                </div>
            ) : (
                <>
                    <div className="gp-section-head">{titolo}</div>
                    {visibili.length === 0 ? (
                        <div className="gp-empty">{vuoto}</div>
                    ) : (
                        <div className="gp-cards-grid">
                            {visibili.map((p) => <CardPercorso key={p.id} p={p} onDetail={onDetail} azioni={azioni(p)} />)}
                        </div>
                    )}
                </>
            )}
            </>
            )}
        </>
    );
};

export default SezioneOggi;
